import { useEffect, useState, useContext } from 'react';

import { UserContext } from 'components/signUpFunnel/Contexts';
import useQuery from 'hooks/useQuery';
import * as ErrorLogging from 'services/ErrorLogging';
import RequestService from 'services/RequestService';

const controlGroup = { isVariant: false };

/**
 * A hook for fetching a user's existing assignment for a given experiment.
 * This hook will NOT assign a user to an experiment. Use `hooks/useAssignExperiment.js` for that.
 *
 * See doc/experiments/README.md for more details.
 *
 * @typedef {Object} ExperimentData
 * @property {boolean} isVariant Whether the experiment group is a variant group
 * @property {string} experimentName The name of the experiment
 * @property {string} experimentGroupId the hash ID of the experiment group
 *
 * @param {string} name The name of the experiment (see: lib/constants/experimentConstants.js)
 * @param {Array} [dependencies=[]] An array of dependencies on which to re-fetch
 * @returns {ExperimentData}
 *
 */
const useFetchExperimentAssignment = (name, dependencies = []) => {
  const { user } = useContext(UserContext) || {};
  const [experimentAssignment, setExperimentAssignment] = useState();
  const { queryParams } = useQuery();

  const experimentParam = queryParams.get(name);

  useEffect(() => {
    if (experimentParam) return;

    const fetchExperimentAssignment = async () => {
      try {
        const response = await RequestService.getExperimentAssignment({ params: { name } });
        if (response.ok) {
          setExperimentAssignment(response.experimentAssignment);
        } else {
          ErrorLogging.captureException(response.error);
        }
      } catch (error) {
        ErrorLogging.captureException(error.message);
      }
    };

    const experimentAssignment = user?.experimentVariants?.find(
      variant => variant.experimentName === name
    );
    if (experimentAssignment) {
      setExperimentAssignment(experimentAssignment);
    } else {
      fetchExperimentAssignment();
    }
  }, [experimentParam, name, user?.experimentVariants, ...dependencies]);

  useEffect(() => {
    const getExperiment = async () => {
      const response = await RequestService.getExperiment({ params: { name } });
      if (response.ok && response.experiment) {
        const group = response.experiment.groups.find(group => group.id === experimentParam);
        if (group) {
          setExperimentAssignment({ isVariant: group.variant, experimentGroupId: group.id });
        }
      }
    };

    if (experimentParam) getExperiment();
  }, [experimentParam, setExperimentAssignment]);

  return experimentAssignment ? experimentAssignment : controlGroup;
};

export default useFetchExperimentAssignment;
